import { ReactElement, useEffect, useMemo, useState } from 'react';
import { grievancesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const OfficeDashboard = (): ReactElement => {
  const { user } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await grievancesAPI.getAllSecured();
        setItems(res.data || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const stats = useMemo(() => {
    const total = items.length;
    const inProgress = items.filter(g => String(g.status).toLowerCase() === 'in-progress').length;
    const resolved = items.filter(g => String(g.status).toLowerCase() === 'resolved').length;
    const pending = items.filter(g => String(g.status).toLowerCase() === 'pending').length;
    return { total, pending, inProgress, resolved };
  }, [items]);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">Office Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white shadow rounded p-4">
          <div className="text-gray-500 text-sm">Total</div>
          <div className="text-3xl font-bold">{stats.total}</div>
        </div>
        <div className="bg-white shadow rounded p-4">
          <div className="text-gray-500 text-sm">Pending</div>
          <div className="text-3xl font-bold">{stats.pending}</div>
        </div>
        <div className="bg-white shadow rounded p-4">
          <div className="text-gray-500 text-sm">In Progress</div>
          <div className="text-3xl font-bold">{stats.inProgress}</div>
        </div>
        <div className="bg-white shadow rounded p-4">
          <div className="text-gray-500 text-sm">Resolved</div>
          <div className="text-3xl font-bold">{stats.resolved}</div>
        </div>
      </div>

      {/* Simple pie representation using CSS circles (no extra lib) */}
      <div className="bg-white shadow rounded p-6">
        <div className="font-semibold mb-3">Status Distribution</div>
        {loading ? (
          <div>Loading…</div>
        ) : stats.total === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p className="text-lg font-medium">No grievances assigned to your office yet</p>
            <p className="text-sm mt-2">Grievances will appear here once they are forwarded to your office by the Ministry Admin.</p>
          </div>
        ) : (
          <div className="flex items-center space-x-6">
            <div className="w-40 h-40 rounded-full" style={{
              background: `conic-gradient(#f59e0b 0 ${(stats.pending / stats.total) * 360}deg, #3b82f6 ${(stats.pending / stats.total) * 360}deg ${(stats.pending + stats.inProgress) / stats.total * 360}deg, #10b981 ${(stats.pending + stats.inProgress) / stats.total * 360}deg 360deg)`
            }} />
            <div>
              <div className="flex items-center space-x-2"><span className="inline-block w-3 h-3 bg-yellow-500 rounded"></span><span>Pending ({stats.pending})</span></div>
              <div className="flex items-center space-x-2"><span className="inline-block w-3 h-3 bg-blue-500 rounded"></span><span>In Progress ({stats.inProgress})</span></div>
              <div className="flex items-center space-x-2"><span className="inline-block w-3 h-3 bg-green-500 rounded"></span><span>Resolved ({stats.resolved})</span></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficeDashboard;
